import React, {useState, useRef, useEffect} from 'react'
import {Modal} from 'react-bootstrap-v5'
import {KTSVG} from '../../_metronic/helpers'
import {StepperComponent} from '../../_metronic/assets/ts/components'
import {Formik} from 'formik'
import {
  ICustomerModel,
  initialCustomer,
  validationCustomer,
} from '../../app/pages/customer/CustomerModel'
import {createCustomer} from '../../app/pages/customer/Api'
import {showErrorToast, showSuccessToast} from '../components/ToastUtils'
import TextInput from '../components/TextInput'
import SelectInput from '../components/SelectInput'
import {COUNTRY_OPTIONS, LICENSE_CLASS, LICENSE_TYPES} from '../models/SharedConstantLookups'
import PhoneInput from '../components/PhoneInput'
import TextAreaInput from '../components/TextAreaInput'

type Props = {
  show: boolean
  modalId: string
  handleClose: () => void
}

const CustomerCreateModal: React.FC<Props> = ({show, modalId, handleClose}) => {
  const stepperRef = useRef<HTMLDivElement | null>(null)
  const stepper = useRef<StepperComponent | null>(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!show && stepper.current) {
      stepper.current = null
    }
  }, [show])

  const loadStepper = () => {
    if (stepperRef.current && !stepper.current) {
      try {
        stepper.current = StepperComponent.createInsance(stepperRef.current as HTMLDivElement)
      } catch (error) {
        console.error('Error initializing stepper:', error)
      }
    }
  }

  const prevStep = () => {
    if (!stepper.current) {
      return
    }

    stepper.current.goPrev()
  }

  const nextStep = () => {
    if (!stepper.current) {
      return
    }

    stepper.current.goNext()
  }

  const handleSave = async (values: ICustomerModel, resetForm: () => void) => {
    setLoading(true)
    try {
      await createCustomer(values)
      showSuccessToast('Müşteri başarıyla oluşturuldu')
      resetForm()
      handleClose()
    } catch (err) {
      showErrorToast('Bir hata oluştu')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal
      id={modalId}
      tabIndex={-1}
      aria-hidden='true'
      dialogClassName='modal-dialog-centered mw-1000px h-auto'
      show={show}
      onHide={handleClose}
      onEntered={loadStepper}
      backdrop='static'
      keyboard={false}
    >
      <div className='container-xxl px-10 py-10'>
        <div className='modal-header py-2 d-flex justify-content-end border-0'>
          <div className='btn btn-icon btn-sm btn-light-primary' onClick={handleClose}>
            <KTSVG className='svg-icon-2' path='/media/icons/duotune/arrows/arr061.svg' />
          </div>
        </div>

        <div className='modal-body'>
          <div
            ref={stepperRef}
            className='stepper stepper-1 d-flex flex-column flex-xl-row flex-row-fluid'
            id='kt_modal_create_app_stepper'
          >
            <div className='d-flex justify-content-center justify-content-xl-start flex-row-auto w-100 w-xl-300px'>
              <div className='stepper-nav d-flex flex-column pt-5'>
                <div className='stepper-item current' data-kt-stepper-element='nav'>
                  <div className='stepper-wrapper'>
                    <div className='stepper-icon'>
                      <i className='stepper-check fas fa-check'></i>
                      <span className='stepper-number'>1</span>
                    </div>
                    <div className='stepper-label'>
                      <h3 className='stepper-title'>Kimlik Bilgileri</h3>
                    </div>
                  </div>
                </div>
                <div className='stepper-item' data-kt-stepper-element='nav'>
                  <div className='stepper-wrapper'>
                    <div className='stepper-icon'>
                      <i className='stepper-check fas fa-check'></i>
                      <span className='stepper-number'>2</span>
                    </div>
                    <div className='stepper-label'>
                      <h3 className='stepper-title'>Ehliyet Bilgileri</h3>
                    </div>
                  </div>
                </div>
                <div className='stepper-item' data-kt-stepper-element='nav'>
                  <div className='stepper-wrapper'>
                    <div className='stepper-icon'>
                      <i className='stepper-check fas fa-check'></i>
                      <span className='stepper-number'>3</span>
                    </div>
                    <div className='stepper-label'>
                      <h3 className='stepper-title'>İletişim Bilgileri</h3>
                    </div>
                  </div>
                </div>
                <div className='stepper-item' data-kt-stepper-element='nav'>
                  <div className='stepper-wrapper'>
                    <div className='stepper-icon'>
                      <i className='stepper-check fas fa-check'></i>
                      <span className='stepper-number'>4</span>
                    </div>
                    <div className='stepper-label'>
                      <h3 className='stepper-title'>Tamamlandı!</h3>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className='d-flex flex-row-fluid justify-content-center'>
              <div className='pb-5 w-100 w-md-400px w-xl-500px' id='kt_modal_create_app_form'>
                <Formik
                  initialValues={initialCustomer}
                  validationSchema={validationCustomer}
                  enableReinitialize
                  onSubmit={(values, {resetForm}) => handleSave(values, resetForm)}
                >
                  {(formik) => {
                    const handleReset = () => {
                      formik.resetForm()
                    }
                    return (
                      <>
                        <div className='pb-5 current' data-kt-stepper-element='content'>
                          <div className='w-100'>
                            <div className='pb-5 pb-lg-10'>
                              <h3 className='fw-bolder text-dark display-6'>Kimlik Bilgileri</h3>
                            </div>
                            <div className='row g-5 align-items-center mb-2'>
                              <TextInput name='firstName' label='Ad' wrapperClassName='col-md-6' />
                              <TextInput
                                name='lastName'
                                label='Soyad'
                                wrapperClassName='col-md-6'
                              />
                              <TextInput
                                max={11}
                                type='number'
                                name='tcKimlikNo'
                                label='T.C. Kimlik No'
                                wrapperClassName='col-md-6'
                              />
                              <TextInput
                                name='dateOfBirth'
                                type='date'
                                label='Doğum Tarihi'
                                wrapperClassName='col-md-6'
                              />
                              <TextInput
                                name='placeOfBirth'
                                label='Doğum Yeri'
                                wrapperClassName='col-md-4'
                              />
                            </div>
                          </div>
                        </div>

                        <div className='pb-5' data-kt-stepper-element='content'>
                          <div className='w-100'>
                            <div className='pb-10 pb-lg-15'>
                              <h3 className='fw-bolder text-dark display-6'>Ehliyet Bilgileri</h3>
                            </div>
                            <div className='row g-5 align-items-center mb-2'>
                              <TextInput
                                name='drivingLicenseNo'
                                label='Ehliyet No'
                                wrapperClassName='col-md-6'
                              />
                              <TextInput
                                name='passportNumber'
                                label='Pasaport No'
                                wrapperClassName='col-md-6'
                              />
                              <TextInput
                                name='drivingLicenseIssueDate'
                                type='date'
                                label='Ehliyet Veriliş Tarihi'
                                wrapperClassName='col-md-6'
                              />
                              <TextInput
                                name='passportIssueDate'
                                type='date'
                                label='Pasaport Veriliş Tarihi'
                                wrapperClassName='col-md-6'
                              />
                              <TextInput
                                name='drivingLicenseIssuedBy'
                                label='Ehliyet Veren Yer'
                                wrapperClassName='col-md-6'
                              />
                              <TextInput
                                name='passportIssuedBy'
                                label='Pasaport Veren Yer'
                                wrapperClassName='col-md-6'
                              />
                              <SelectInput
                                name='drivingLicenseClass'
                                label='Ehliyet Sınıfı'
                                wrapperClassName='col-md-6'
                                options={LICENSE_CLASS}
                              />
                              <SelectInput
                                name='licenseCountry'
                                label='Ehliyet Ülkesi'
                                wrapperClassName='col-md-6'
                                options={COUNTRY_OPTIONS}
                              />
                              <SelectInput
                                name='licenseType'
                                label='Lisans Tipi'
                                wrapperClassName='col-md-6'
                                options={LICENSE_TYPES}
                              />
                            </div>
                          </div>
                        </div>

                        <div className='pb-5' data-kt-stepper-element='content'>
                          <div className='w-100'>
                            <div className='pb-10 pb-lg-15'>
                              <h3 className='fw-bolder text-dark display-6'>İletişim Bilgileri</h3>
                            </div>
                            <div className='row g-5 align-items-center mb-2'>
                              <PhoneInput name='gsm1' label='GSM 1' wrapperClassName='col-md-6' />
                              <PhoneInput name='gsm2' label='GSM 2' wrapperClassName='col-md-6' />
                              <PhoneInput
                                name='phone'
                                label='Telefon'
                                wrapperClassName='col-md-6'
                              />
                              <TextInput
                                name='emailAddress'
                                label='E-posta'
                                wrapperClassName='col-md-6'
                              />
                              <TextInput name='city' label='Şehir' wrapperClassName='col-md-6' />
                              <TextInput name='district' label='İlçe' wrapperClassName='col-md-6' />
                              <TextAreaInput
                                name='address'
                                label='Ev Adresi'
                                wrapperClassName='col-md-12'
                              />
                            </div>
                          </div>
                        </div>
                        <div className='pb-5' data-kt-stepper-element='content'>
                          <div className='w-100'>
                            <div className='pb-10 pb-lg-15'>
                              <h3 className='fw-bolder text-dark display-6'>Ön İzleme</h3>
                              <div className='text-muted fw-bold fs-3'>
                                Bilgileri kontrol edip kaydete basınız!
                              </div>
                            </div>
                            <h4 className='fw-bolder mb-3'>Kimlik Bilgileri</h4>
                            <div className='text-gray-600 fw-bold lh-lg mb-8'>
                              <div>{formik.values.firstName}</div>
                              <div>{formik.values.lastName}</div>
                              <div>{formik.values.tcKimlikNo}</div>
                              <div>{formik.values.dateOfBirth}</div>
                            </div>
                            <h4 className='fw-bolder mb-3'>Ehliyet & Pasaport Bilgileri</h4>
                            <div className='text-gray-600 fw-bold lh-lg mb-8'>
                              <div>{formik.values.drivingLicenseNo}</div>
                              <div>{formik.values.drivingLicenseIssueDate}</div>
                              <div>{formik.values.passportNumber}</div>
                              <div>{formik.values.passportIssueDate}</div>
                            </div>
                            <h4 className='fw-bolder mb-3'>İletişim Bilgileri</h4>
                            <div className='text-gray-600 fw-bold lh-lg mb-8'>
                              <div>{formik.values.phone}</div>
                              <div>{formik.values.gsm1}</div>
                              <div>{formik.values.city}</div>
                              <div>{formik.values.district}</div>
                              <div>{formik.values.address}</div>
                            </div>
                          </div>
                        </div>

                        <div className='d-flex justify-content-between pt-10'>
                          <div className='mr-2'>
                            <button
                              type='button'
                              className='btn btn-lg btn-light-primary fw-bolder py-4 pe-8 me-3'
                              data-kt-stepper-action='previous'
                              onClick={prevStep}
                            >
                              <KTSVG
                                path='/media/icons/duotune/arrows/arr063.svg'
                                className='svg-icon-3 me-1'
                              />{' '}
                              Geri
                            </button>
                          </div>
                          <div>
                            <button
                              type='button'
                              className='btn btn-lg btn-light btn-active-light-primary fw-bolder py-4 ps-8 me-3'
                              data-kt-stepper-action='submit'
                              onClick={() => formik.resetForm()}
                            >
                              Temizle
                            </button>
                            <button
                              type='button'
                              className='btn btn-lg btn-primary fw-bolder py-4 ps-8 me-3'
                              data-kt-stepper-action='submit'
                              onClick={() => formik.handleSubmit()}
                              disabled={loading}
                              title={loading ? 'Kaydediliyor...' : 'Kaydet'}
                            >
                              Kaydet
                            </button>
                            <button
                              type='button'
                              className='btn btn-lg btn-primary fw-bolder py-4 ps-8 me-3'
                              data-kt-stepper-action='next'
                              onClick={nextStep}
                            >
                              Devam{' '}
                              <KTSVG
                                path='/media/icons/duotune/arrows/arr064.svg'
                                className='svg-icon-3 ms-1'
                              />
                            </button>
                          </div>
                        </div>
                      </>
                    )
                  }}
                </Formik>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  )
}

export {CustomerCreateModal}
